import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIVE_GREEN, getStatusChipSx } from '../../constants/statusColors';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  CloudUploadOutlined as ImportIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import StatCard from '../../components/common/StatCard';
import FilterPanel from '../../components/common/FilterPanel';
import DataTable from '../../components/common/DataTable';
import CommitteeImportReviewModal from '../../components/committees/CommitteeImportReviewModal';
import { csAdminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { mapCommitteeFromApi, mapCompanyFromApi } from '../../utils/apiMappers';
import { useLookups } from '../../hooks/useLookups';
import {
  COMMITTEE_TYPE_FILTER_OPTIONS,
  COMMITTEE_TYPES,
  committeeTypesMatch,
  committeeTypeLabel,
} from '../../constants/committeeTypes';
import {
  downloadCommitteeTemplate,
  parseImportedCommitteeRow,
  validateImportedCommitteeRow,
  calculateCommitteeSimilarityScore,
  buildCommitteeCreatePayload,
  isEmptyCommitteeRow,
  isCommitteeTemplateHelperRow,
} from '../../utils/committeeImportTemplate';

const CommitteesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { items: lookupCommitteeTypes } = useLookups('COMMITTEE_TYPE');
  const committeeTypes = lookupCommitteeTypes.length
    ? lookupCommitteeTypes.map((t) => ({ code: t.code, label: t.label }))
    : COMMITTEE_TYPES;
  const [loading, setLoading] = useState(true); // Updated with light icon badges
  const [error, setError] = useState('');
  const [committees, setCommittees] = useState([]);
  const [filteredCommittees, setFilteredCommittees] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importedCommittees, setImportedCommittees] = useState([]);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    totalMembers: 0,
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => {
    fetchCommittees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter, statusFilter, companyFilter, committees]);

  const fetchCommittees = async () => {
    try {
      setLoading(true);

      const response = await csAdminApi.getCommittees({
          company_id: companyFilter || undefined,
          status: statusFilter || undefined,
          search: searchQuery || undefined,
        });
        const list = (response.data?.data || []).map(mapCommitteeFromApi);
        setCommittees(list);
        setSummary(
          response.data?.summary || {
            total: list.length,
            active: list.filter((c) => c.status === 'ACTIVE').length,
            totalMembers: list.reduce((s, c) => s + (c.memberCount || 0), 0),
          }
        );
        if (response.data?.meta?.committeesSchemaRequired) {
          setError('Committees tables not installed. Run backend/scripts/csadmin/01-committees-schema.sql');
        } else {
          setError('');
        }
    } catch (err) {
      console.error('Error fetching committees:', err);
      setError('Failed to load committees');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...committees];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (committee) =>
          committee.name.toLowerCase().includes(query) ||
          committee.type.toLowerCase().includes(query) ||
          committee.chairperson.toLowerCase().includes(query) ||
          committee.company.toLowerCase().includes(query)
      );
    }

    // Company filter
    if (companyFilter) {
      filtered = filtered.filter((committee) => committee.company === companyFilter);
    }

    // Type filter (handles legacy full labels and short mock codes)
    if (typeFilter) {
      filtered = filtered.filter((committee) =>
        committeeTypesMatch(committee.typeCode || committee.type, typeFilter)
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((committee) => committee.status === statusFilter);
    }

    setFilteredCommittees(filtered);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'company') {
      setCompanyFilter(value);
    } else if (filterName === 'type') {
      setTypeFilter(value);
    } else if (filterName === 'status') {
      setStatusFilter(value);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCompanyFilter('');
    setTypeFilter('');
    setStatusFilter('');
  };

  const handleDownloadTemplate = () => {
    downloadCommitteeTemplate();
    enqueueSnackbar('Template downloaded', { variant: 'success' });
  };

  const findMatchingCommittees = (importedCommittee, existingCommittees) => {
    return existingCommittees
      .map((existing) => ({
        ...existing,
        score: calculateCommitteeSimilarityScore(importedCommittee, existing),
      }))
      .filter((match) => match.score >= 50)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const performImport = async (committeesToImport) => {
    try {
      setImporting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const committee of committeesToImport) {
        try {
          if (committee.resolution?.action === 'skip'
            || committee.resolution?.action?.startsWith('map-')) {
            continue;
          }
          if (!committee.resolution && committee.status === 'duplicate') {
            continue;
          }

          const response = await csAdminApi.createCommittee(buildCommitteeCreatePayload(committee));
          if (response.status !== 201 && response.status !== 200) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }
          if (response.data?.success === false) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }
          successCount += 1;
        } catch (err) {
          const errorMsg = err.response?.data?.error?.message
            || err.response?.data?.message
            || err.message
            || 'Unknown error';
          errors.push(`${committee.name}: ${errorMsg}`);
          errorCount += 1;
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Successfully imported ${successCount} committee(ies)`, { variant: 'success' });
        fetchCommittees();
      }
      if (errorCount > 0) {
        enqueueSnackbar(`Failed to import ${errorCount} committee(ies). Check console for details.`, { variant: 'error' });
        errors.forEach((entry) => console.error('  -', entry));
      }

      setImportModalOpen(false);
      setImportedCommittees([]);
    } catch (err) {
      enqueueSnackbar(`Error during import: ${err.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    try {
      setImporting(true);
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet);

          if (rows.length === 0) {
            enqueueSnackbar('No data found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          let companiesMap = {};
          let existingCommittees = [];
          {
            try {
              const [companiesRes, committeesRes] = await Promise.all([
                csAdminApi.getCompanies(),
                csAdminApi.getCommittees(),
              ]);
              const companiesList = (companiesRes.data?.data || []).map(mapCompanyFromApi);
              companiesList.forEach((company) => {
                companiesMap[company.name.toLowerCase().trim()] = company.id;
              });
              existingCommittees = (committeesRes.data?.data || []).map(mapCommitteeFromApi);
            } catch (err) {
              enqueueSnackbar('Failed to load reference data. Please try again.', { variant: 'error' });
              setImporting(false);
              return;
            }
          }

          const processedCommittees = [];
          for (let i = 0; i < rows.length; i++) {
            const parsed = parseImportedCommitteeRow(rows[i], i);
            if (isEmptyCommitteeRow(parsed) || isCommitteeTemplateHelperRow(parsed)) {
              continue;
            }

            let companyId = null;
            let companyLookupError = null;
            if (!parsed.companyName) {
              companyLookupError = 'Company Name is required';
            } else {
              const companyNameLower = parsed.companyName.toLowerCase().trim();
              companyId = companiesMap[companyNameLower];
              if (!companyId) {
                const suggestions = Object.keys(companiesMap).filter((name) =>
                  name.includes(companyNameLower) || companyNameLower.includes(name));
                const suggestion = suggestions.length > 0
                  ? ` Did you mean: ${suggestions.slice(0, 3).join(', ')}?`
                  : '';
                companyLookupError = `Company "${parsed.companyName}" not found.${suggestion}`;
              }
            }

            parsed.companyId = companyId;

            const validation = validateImportedCommitteeRow(parsed, { committeeTypes });

            let status = 'ready';
            let errors = [];
            let matches = [];

            if (!validation.isValid) {
              status = 'error';
              errors = validation.errors;
            } else if (companyLookupError) {
              status = 'error';
              errors = [companyLookupError];
            } else {
              matches = findMatchingCommittees(parsed, existingCommittees);
              if (matches.length > 0) {
                status = 'duplicate';
              }
            }

            processedCommittees.push({ ...parsed, status, errors, matches });
          }

          if (processedCommittees.length === 0) {
            enqueueSnackbar('No committee rows found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          const hasIssues = processedCommittees.some((c) => c.status !== 'ready');
          if (hasIssues) {
            setImportedCommittees(processedCommittees);
            setImportModalOpen(true);
          } else {
            await performImport(processedCommittees);
          }
        } catch (err) {
          enqueueSnackbar('Error processing file. Please check the format.', { variant: 'error' });
        } finally {
          setImporting(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      enqueueSnackbar('Error reading file', { variant: 'error' });
      setImporting(false);
    }
  };

  const columns = [
    {
      id: 'name',
      label: 'Committee Name',
      minWidth: 280,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: '#F5F5F5',
              color: '#616161',
            }}
          >
            <GroupsIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {committeeTypeLabel(row.typeCode || row.type)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'chairperson',
      label: 'Chairperson',
      minWidth: 180,
    },
    {
      id: 'memberCount',
      label: 'Members',
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'quorum',
      label: 'Quorum',
      minWidth: 130,
    },
    {
      id: 'nextMeeting',
      label: 'Next Meeting',
      minWidth: 150,
      render: (row) => {
        if (!row.nextMeeting) return '-';
        const date = new Date(row.nextMeeting);
        return date.toLocaleDateString('en-GB');
      },
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (row) => (
        <Chip
          label={row.status}
          sx={{
            ...getStatusChipSx(row.status),
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            borderRadius: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
          size="small"
        />
      ),
    },
  ];

  const companyFilterOptions = React.useMemo(() => {
    const names = [...new Set(committees.map((c) => c.company).filter(Boolean))].sort();
    return names.map((name) => ({ value: name, label: name }));
  }, [committees]);

  const filterOptions = [
    {
      label: 'Company',
      name: 'company',
      value: companyFilter,
      options: companyFilterOptions,
    },
    {
      label: 'Committee Type',
      name: 'type',
      value: typeFilter,
      options: COMMITTEE_TYPE_FILTER_OPTIONS,
    },
    {
      label: 'Status',
      name: 'status',
      value: statusFilter,
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
        { value: 'PENDING', label: 'Pending' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Committees Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage board committees, members, and meetings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
            disabled={importing}
          >
            Import Committees
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/committees/create')}
          >
            Create Committee
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Committees"
            value={summary.total}
            icon={GroupsIcon}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Active Committees"
            value={summary.active}
            icon={CheckCircleIcon}
            color={ACTIVE_GREEN}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Members"
            value={summary.totalMembers}
            icon={PeopleIcon}
            color="#FF9800"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search by committee name, type, or chairperson..."
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Committees Table */}
      <DataTable
        rows={filteredCommittees}
        columns={columns}
        onRowClick={(row) => navigate(`/committees/${row.id}`)}
        emptyMessage="No committees found"
      />

      <CommitteeImportReviewModal
        open={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportedCommittees([]);
        }}
        importedCommittees={importedCommittees}
        onImport={performImport}
      />
    </Container>
  );
};

export default CommitteesList;
